import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { editorService } from '../services/api';
import {
  ArrowLeft,
  User,
  Type,
  FileText,
  Sparkles,
  Info,
  Save,
  ChevronDown,
  Check
} from 'lucide-react';
import './EditorForm.css';

const EDITOR_STYLE_OPTIONS = [
  'Formal',
  'Casual',
  'Technical',
  'Creative',
  'Journalistic',
  'Academic'
];

const getStyleIconColor = (styleName) => {
  const map = {
    'Formal': { bg: '#e0f2fe', color: '#0284c7', letter: 'F' },
    'Casual': { bg: '#ffedd5', color: '#ea580c', letter: 'C' },
    'Technical': { bg: '#f3e8ff', color: '#9333ea', letter: 'T' },
    'Creative': { bg: '#fae8ff', color: '#c026d3', letter: 'C' },
    'Journalistic': { bg: '#ccfbf1', color: '#0d9488', letter: 'J' },
    'Academic': { bg: '#dcfce7', color: '#16a34a', letter: 'A' },
  };
  return map[styleName] || { bg: '#f1f5f9', color: '#475569', letter: styleName ? styleName[0] : 'S' };
};

const StyleSelect = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const selectedOption = options.find(o => o.value === value);

  return (
    <div className="editorform-custom-select" ref={containerRef}>
      <div 
        className={`editorform-select-trigger ${isOpen ? 'open' : ''} ${value ? 'has-value' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="editorform-select-trigger-content">
          <Type size={18} strokeWidth={2} className="editorform-field-icon" />
          <span className="editorform-select-value">
            {selectedOption ? selectedOption.label : 'Select a language style'}
          </span>
        </div>
        <ChevronDown size={18} className="editorform-chevron" />
      </div>

      {isOpen && (
        <div className="editorform-options-dropdown">
          {options.map((opt) => {
            const colors = getStyleIconColor(opt.value);
            return (
              <div 
                key={opt.value}
                className={`editorform-option ${value === opt.value ? 'selected' : ''}`}
                onClick={() => {
                  onChange({ target: { name: 'style', value: opt.value } });
                  setIsOpen(false);
                }}
              >
                <div className="editorform-option-icon" style={{ background: colors.bg, color: colors.color }}>
                  {colors.letter}
                </div>
                <span>{opt.label}</span>
                {value === opt.value && <Check size={16} className="editorform-check" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const EditorForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    style: '',
    prompt: '',
    description: '',
    useWebSearch: false,
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEditor = async () => {
      try {
        setFetching(true);
        const response = await editorService.getById(id);
        setFormData({
          name: response.data.name || '',
          style: response.data.style || '',
          prompt: response.data.prompt || '',
          description: response.data.description || '',
          useWebSearch: Boolean(response.data.useWebSearch),
        });
        setError(null);
      } catch (err) {
        setError('Failed to load Editor details');
        console.error('Error fetching Editor:', err);
      } finally {
        setFetching(false);
      }
    };

    if (isEdit) {
      fetchEditor();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.name || !formData.style || !formData.prompt) {
      setError('Name, style, and prompt are required fields');
      setLoading(false);
      return;
    }

    try {
      if (isEdit) {
        await editorService.update(id, formData);
      } else {
        await editorService.create(formData);
      }
      navigate('/editors');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save Editor');
      console.error('Error saving Editor:', err);
    } finally {
      setLoading(false);
    }
  };

  if (id && fetching) {
    return (
      <div className="app-loading-container">
        <div className="app-loading-spinner" />
        <p>Loading Editor details...</p>
      </div>
    );
  }

  const styleOptions = EDITOR_STYLE_OPTIONS.map(s => ({ value: s, label: s }));

  return (
    <div className="editorform-page">
      <div className="editorform-main">
        {/* Top Button */}
        <div className="editorform-top-actions">
          <button type="button" className="editorform-btn-back" onClick={() => navigate('/editors')}>
            <ArrowLeft size={16} strokeWidth={2} />
            Back to List
          </button>
        </div>

        {/* Form Container */}
        <div className="editorform-card">
          <div className="editorform-header-section">
            <div className="editorform-avatar-icon">
              <User size={24} strokeWidth={2} />
            </div>
            <div className="editorform-header-text">
              <h1>{isEdit ? 'Edit Editor' : 'Create New Editor'}</h1>
              <p>{isEdit ? 'Update the details for this AI editorial persona.' : 'Add a new AI editorial persona with a specific language style.'}</p>
            </div>
          </div>

          {error && <div className="editorform-error">{error}</div>}

          <form onSubmit={handleSubmit} className="editorform-form">
            
            <div className="editorform-row">
              {/* Name */}
              <div className="editorform-group">
                <label>Name <span className="req">*</span></label>
                <div className="editorform-input-wrapper">
                  <User size={18} className="editorform-field-icon" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter Editor name"
                    className="editorform-input"
                    required
                  />
                </div>
                <p className="editorform-hint">Enter a unique and descriptive name for the Editor.</p>
              </div>

              {/* Language Style */}
              <div className="editorform-group">
                <label>Language Style <span className="req">*</span></label>
                <StyleSelect 
                  value={formData.style} 
                  onChange={handleChange} 
                  options={styleOptions} 
                />
              </div>
            </div>

            {/* Description */}
            <div className="editorform-group">
              <label>Description</label>
              <div className="editorform-textarea-wrapper">
                <FileText size={18} className="editorform-field-icon top-icon" />
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter description (optional)"
                  className="editorform-textarea"
                  maxLength={500}
                  rows={4}
                />
                <div className="editorform-char-count">{formData.description.length}/500</div>
              </div>
            </div>

            {/* Checkbox */}
            <div className="editorform-group">
              <label className="editorform-checkbox-label">
                <div className="editorform-checkbox-wrap">
                  <input
                    type="checkbox"
                    name="useWebSearch"
                    checked={Boolean(formData.useWebSearch)}
                    onChange={handleChange}
                  />
                  <div className="editorform-checkbox-custom">
                    {formData.useWebSearch && <Check size={12} strokeWidth={3} />}
                  </div>
                </div>
                <span>Enable optional web search for this editor persona</span>
                <Info size={14} className="editorform-info-icon" />
              </label>
            </div>

            {/* Prompt */}
            <div className="editorform-group">
              <label>AI Prompt (Editorial Guidelines) <span className="req">*</span></label>
              <div className="editorform-textarea-wrapper">
                <Sparkles size={18} className="editorform-field-icon top-icon" />
                <textarea
                  name="prompt"
                  value={formData.prompt}
                  onChange={handleChange}
                  placeholder="Enter the prompt that defines how this editor moderates language style"
                  className="editorform-textarea"
                  required
                  rows={4}
                />
              </div>
              <p className="editorform-hint">This prompt will be used to define the AI's editorial behavior when moderating article language and style.</p>
              <p className="editorform-hint">Runtime line added automatically: English style + English level + regenerate instruction.</p>
            </div>

            {/* Actions */}
            <div className="editorform-footer-actions">
              <button
                type="button"
                className="editorform-btn-cancel"
                onClick={() => navigate('/editors')}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="editorform-btn-submit"
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEdit ? (
                  <>
                    <Save size={16} strokeWidth={2} />
                    Update Editor
                  </>
                ) : (
                  <>
                    <Save size={16} strokeWidth={2} />
                    Create Editor
                  </>
                ))}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default EditorForm;
